import React, { useState } from "react";
import apiClient from '../services/apiClient';
import { useQuery } from '@tanstack/react-query';
import { DateRangeFilter } from '../components/ui/DateRangeFilter';

const fmtAmt = (n: number) =>
  n === 0 ? "0.00" : n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ProfitAnalytics: React.FC = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("all");

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['profit_analytics', startDate, endDate],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      let url = `/getProfitAnalyticsReport?`;
      if (startDate) url += `startDate=${encodeURIComponent(startDate)}&`;
      if (endDate) url += `endDate=${encodeURIComponent(endDate)}`;

      const res = await apiClient.get(url, { headers: { Authorization: `Bearer ${token}` } });
      return res.data?.data || { deposits: {}, withdrawals: {}, ggr: {}, affiliatePayouts: {} };
    }
  });

  const currencies = Array.from(new Set([
    ...Object.keys(analytics?.deposits || {}),
    ...Object.keys(analytics?.withdrawals || {}),
    ...Object.keys(analytics?.ggr || {}),
    ...Object.keys(analytics?.affiliatePayouts || {})
  ])).sort();

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between bg-[#1E2437] p-4 rounded-xl mb-6 shadow-sm gap-4">
        <div className="w-full md:w-auto">
          <h2 className="text-xl font-bold text-white">GGR & Platform Profit</h2>
          <p className="text-sm text-gray-400">Dashboard for Gross Gaming Revenue, ROI, and Affiliate Payouts.</p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <select 
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className="bg-[#151929] border border-gray-700 text-white text-sm rounded-xl px-4 py-2 outline-none focus:border-[#646ECD] transition-colors cursor-pointer flex-1 md:flex-initial"
          >
            <option value="all">All Currencies</option>
            {currencies.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <div className="flex-1 md:flex-initial">
            <DateRangeFilter 
              onFilterChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }} 
            />
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div className="bg-[#1E2437] p-8 rounded-xl flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-300">Calculating platform metrics...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {currencies
            .filter(c => selectedCurrency === "all" || c === selectedCurrency)
            .map(currency => {
              const deps = analytics?.deposits?.[currency] || 0;
              const wds = analytics?.withdrawals?.[currency] || 0;
              const ggr = analytics?.ggr?.[currency] || { bets: 0, wins: 0, profit: 0 };
            const aff = analytics?.affiliatePayouts?.[currency] || 0;

            return (
              <div key={currency} className="bg-[#1E2437] rounded-xl overflow-hidden border border-gray-800">
                <div className="bg-[#1a2035] px-6 py-4 border-b border-gray-800 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                    {currency} Metrics
                  </h3>
                </div>
                
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Deposits & Withdrawals */}
                  <div className="bg-[#151929] p-4 rounded-lg border border-gray-800">
                    <p className="text-sm text-gray-400 mb-1">Total Deposits</p>
                    <p className="text-xl font-bold text-emerald-400">{fmtAmt(deps)}</p>
                  </div>
                  <div className="bg-[#151929] p-4 rounded-lg border border-gray-800">
                    <p className="text-sm text-gray-400 mb-1">Total Withdrawals</p>
                    <p className="text-xl font-bold text-red-400">{fmtAmt(wds)}</p>
                  </div>

                  {/* GGR */}
                  <div className="bg-[#151929] p-4 rounded-lg border border-gray-800 lg:col-span-2 row-span-2 flex flex-col justify-between">
                    <div>
                      <p className="text-sm text-gray-400 mb-4">Gross Gaming Revenue (GGR)</p>
                      <div className="flex items-end justify-between">
                        <div className="flex flex-col gap-4">
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Bets</p>
                            <p className="text-lg font-semibold text-white">{fmtAmt(ggr.bets)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Wins Paid</p>
                            <p className="text-lg font-semibold text-gray-300">{fmtAmt(ggr.wins)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-amber-500 uppercase tracking-wide font-bold mb-1">House Profit</p>
                          <p className={`text-3xl font-black ${ggr.profit >= 0 ? 'text-amber-400' : 'text-red-500'}`}>
                            {ggr.profit >= 0 ? '+' : ''}{fmtAmt(ggr.profit)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-800">
                      <div className="w-full bg-gray-800 rounded-full h-2.5">
                        <div 
                          className="bg-amber-400 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, Math.max(0, (ggr.profit / (ggr.bets || 1)) * 100))}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-right">
                        Margin: {((ggr.profit / (ggr.bets || 1)) * 100).toFixed(2)}%
                      </p>
                    </div>
                  </div>

                  {/* Affiliate */}
                  <div className="bg-[#151929] p-4 rounded-lg border border-purple-500/20 col-span-1 md:col-span-2">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-purple-400 mb-1 font-medium">Affiliate Payouts</p>
                        <p className="text-xl font-bold text-purple-300">{fmtAmt(aff)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Payouts vs Profit</p>
                        <p className="text-sm font-semibold text-gray-300">
                          {ggr.profit > 0 ? ((aff / ggr.profit) * 100).toFixed(1) : 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {currencies.length === 0 && (
            <div className="text-center py-12 bg-[#1E2437] rounded-xl border border-gray-800">
              <p className="text-gray-400">No financial data found for the selected dates.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfitAnalytics;
